import { useEffect, useRef, useState } from 'react';

/** Le silence attendu entre la dernière frappe et la requête envoyée. */
const DELAI_MS = 300;

interface UseRechercheConversationsParams {
    /** Le terme retrouvé en revenant d'une conversation (audit 2026-09-23, B2). */
    initial: string;
    /** Appelé quand le terme RETENU change — jamais à chaque frappe. Repart en page 1. */
    onTermeChange: () => void;
}

/**
 * La recherche de conversations dans la liste — champ + minuteur, extraits de
 * `ListeConversations` (déjà à son plafond de lignes, #82).
 *
 * Deux valeurs séparées à dessein : `saisie` est ce que le champ AFFICHE, mise à
 * jour à chaque frappe pour rester réactive ; `terme` est ce que la requête
 * SERVEUR utilise, retardé de 300 ms pour ne pas envoyer une requête par lettre
 * tapée. Un seul minuteur à la fois : retaper avant qu'il sonne le repousse, il
 * ne s'additionne pas — `useEffect` nettoie le précédent à chaque frappe.
 *
 * `onTermeChange` passe par une ref, comme `recuLe` dans `useActualisationListe` :
 * l'effet qui la déclenche ne dépend que de `terme`, pas de l'identité de la
 * fonction que l'appelant recrée à chaque rendu — sans quoi il se redéclencherait
 * à chaque frappe de `saisie`, bien avant que `terme` n'ait lui-même bougé.
 */
export function useRechercheConversations({ initial, onTermeChange }: UseRechercheConversationsParams) {
    const [saisie, setSaisie] = useState(initial);
    const [terme, setTerme] = useState(initial);

    useEffect(() => {
        const minuteur = setTimeout(() => setTerme(saisie), DELAI_MS);
        return () => clearTimeout(minuteur);
    }, [saisie]);

    const rappel = useRef(onTermeChange);
    rappel.current = onTermeChange;

    // Le premier rendu ne doit RIEN réinitialiser : `terme` y vaut déjà `initial`,
    // et la page part déjà à 1 par construction — un appel ici ne ferait que
    // vider `pagesVues` une fois de plus pour rien.
    const premier = useRef(true);
    useEffect(() => {
        if (premier.current) {
            premier.current = false;
            return;
        }
        rappel.current();
    }, [terme]);

    return { saisie, setSaisie, terme, effacer: () => setSaisie('') };
}
